import React, { useState, useEffect } from 'react';
import { Copy, ChevronDown } from 'lucide-react';
import { Analytics } from '@vercel/analytics/react';

import './index.css';

// Remove these imports
// import { Menu, Transition } from '@headlessui/react'
// import { ChevronDownIcon } from '@heroicons/react/20/solid'
// import { Fragment } from 'react'

// Add new interface for menu items
interface MenuItemType {
  title: string;
  subtitle: string;
}

const transformations: MenuItemType[] = [
  {
    title: 'Hebrew Keyboard',
    subtitle: 'English keyboard to Hebrew (קראטון)'
  },
  {
    title: 'English Keyboard',
    subtitle: 'Hebrew keyboard to English (QWERTY)'
  },
  {
    title: 'Remove Niqqud',
    subtitle: 'Strip vowel points and diacritics'
  },
  {
    title: 'Square Hebrew',
    subtitle: 'Convert Paleo to modern Hebrew'
  },
  {
    title: 'Paleo-Hebrew',
    subtitle: 'Convert to ancient Hebrew script'
  },
  {
    title: 'Gematria',
    subtitle: 'Calculate numerological value'
  }
];

// System font fallback
const systemFontStack = "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'";

// Hebrew -> Paleo map with regular letters only
const hebrewToPaleoMap: Record<string, string> = {
  'א': '𐤀', 'ב': '𐤁', 'ג': '𐤂', 'ד': '𐤃', 'ה': '𐤄', 'ו': '𐤅', 'ז': '𐤆',
  'ח': '𐤇', 'ט': '𐤈', 'י': '𐤉', 'כ': '𐤊', 'ל': '𐤋', 'מ': '𐤌',
  'נ': '𐤍', 'ס': '𐤎', 'ע': '𐤏', 'פ': '𐤐',
  'צ': '𐤑', 'ק': '𐤒', 'ר': '𐤓', 'ש': '𐤔', 'ת': '𐤕',
  // Map final letters to regular letters
  'ך': '𐤊', 'ם': '𐤌', 'ן': '𐤍', 'ף': '𐤐', 'ץ': '𐤑'
};

// Create reverse mapping for Paleo to Square Hebrew
const paleoToHebrewMap: Record<string, string> = Object.entries(hebrewToPaleoMap)
  .reduce((acc, [key, value]) => ({
    ...acc,
    [value]: key
  }), {});

// Hebrew -> English (QWERTY) dict for toLatinQwerty:
// If the user wants a robust mapping for each letter, define here.
// Example from earlier snippet:
const hebrewToEnglishDict: Record<string, string> = {
  '/': 'q', '׳': 'w', 'ק': 'e', 'ר': 'r', 'א': 't', 'ט': 'y', 'ו': 'u', 'ן': 'i',
  'ם': 'o', 'פ': 'p', 'ש': 'a', 'ד': 's', 'ג': 'd', 'כ': 'f', 'ע': 'g', 'י': 'h',
  'ח': 'j', 'ל': 'k', 'ך': 'l', 'ף': ';', 'ז': 'z', 'ס': 'x', 'ב': 'c', 'ה': 'v',
  'נ': 'b', 'מ': 'n', 'צ': 'm', 'ת': ',', ' ': ' ', ',': '<', '.': '>', "'": '"',
  '"': '\'', '(': ')', ')': '(', '\n': '\n',
};

// Build an English->Hebrew dict if we want typed English to become Hebrew.
function buildEnglishToHebrewDict(): Record<string, string> {
  const base: Record<string, string> = {
    'q': '/', 'w': '׳', 'e': 'ק', 'r': 'ר', 't': 'א', 'y': 'ט', 'u': 'ו', 'i': 'ן',
    'o': 'ם', 'p': 'פ', 'a': 'ש', 's': 'ד', 'd': 'ג', 'f': 'כ', 'g': 'ע', 'h': 'י',
    'j': 'ח', 'k': 'ל', 'l': 'ך', ';': 'ף', 'z': 'ז', 'x': 'ס', 'c': 'ב', 'v': 'ה',
    'b': 'נ', 'n': 'מ', 'm': 'צ', ',': 'ת', '.': ' ', '<': ',', '>': '.', ' ': ' ',
    "'": ',', '"': '"', '(': ')', ')': '(', '\n': '\n',
  };
  const result: Record<string, string> = { ...base };
  // uppercase => same letter in Hebrew?
  Object.keys(base).forEach(k => {
    if (/^[a-z]$/.test(k)) {
      const up = k.toUpperCase();
      result[up] = base[k];
    }
  });
  return result;
}

const englishToHebrewDict = buildEnglishToHebrewDict();

// For removing Niqqud:
function removeNiqqud(s: string) {
  return s.replace(/[\u0591-\u05C7]/g, '');
}

// Simple gematria table
const hebrewGematriaMap: Record<string, number> = {
  'א': 1, 'ב': 2, 'ג': 3, 'ד': 4, 'ה': 5,
  'ו': 6, 'ז': 7, 'ח': 8, 'ט': 9, 'י': 10,
  'כ': 20, 'ך': 20, 'ל': 30, 'מ': 40, 'ם': 40,
  'נ': 50, 'ן': 50, 'ס': 60, 'ע': 70, 'פ': 80,
  'ף': 80, 'צ': 90, 'ץ': 90, 'ק': 100, 'ר': 200,
  'ש': 300, 'ת': 400,
};

////////////////////////////////////////////
// Transformation Helpers
////////////////////////////////////////////

// Hebrew -> English (QWERTY)
function toLatinQwerty(text: string): string {
  const stripped = removeNiqqud(text);
  return stripped
    .split('')
    .map(ch => hebrewToEnglishDict[ch] || ch)
    .join('');
}

// Hebrew -> Paleo Hebrew
function toPaleoHebrew(text: string): string {
  const stripped = removeNiqqud(text);
  return stripped.split('').map(ch => hebrewToPaleoMap[ch] || ch).join('');
}

// English -> Hebrew (קראטון)
function toKratonHebrew(text: string): string {
  // For brevity, let's do the dictionary approach from earlier:
  return text.split('').map(ch => englishToHebrewDict[ch] || ch).join('');
}

// Summation gematria
function toGematria(text: string): string {
  let sum = 0;
  for (const ch of text) {
    if (hebrewGematriaMap[ch]) {
      sum += hebrewGematriaMap[ch];
    }
  }
  return String(sum);
}

// Add transformation function
function paleoToSquare(text: string): string {
  return Array.from(text)
    .map(char => {
      const regularChar = paleoToHebrewMap[char];
      // Map final letters to regular ones
      if (regularChar === 'ך') return 'כ';
      if (regularChar === 'ם') return 'מ';
      if (regularChar === 'ן') return 'נ';
      if (regularChar === 'ף') return 'פ';
      if (regularChar === 'ץ') return 'צ';
      return regularChar || char;
    })
    .join('');
}

// Identify Hebrew vs English letters:
function isHebrewLetter(ch: string): boolean {
  const code = ch.charCodeAt(0);
  return code >= 0x0590 && code <= 0x05FF;
}
function isEnglishLetter(ch: string): boolean {
  const code = ch.charCodeAt(0);
  return (code >= 65 && code <= 90) || (code >= 97 && code <= 122);
}

// Decide if a character can be transformed for the given transform:
function canTransform(ch: string, transform: string): boolean {
  switch (transform) {
    case 'English Keyboard':
    case 'Paleo-Hebrew':
    case 'Remove Niqqud':
      return isHebrewLetter(ch);

    case 'Hebrew Keyboard':
    case 'Gematria':
      // For Gematria or Hebrew(קראטון) we consider Hebrew letters transformable.
      // For Hebrew(קראטון), we also consider English letters.
      return (
        isHebrewLetter(ch) || (transform === 'Hebrew Keyboard' && isEnglishLetter(ch))
      );

    default:
      return false;
  }
}

// Highlight input text: if transformable => green, else => red
function highlightInput(text: string, transform: string) {
  return (
    <>
      {text.split('').map((ch, idx) => {
        if (ch === '\n') return <br key={idx}/>;
        const ok = canTransform(ch, transform);
        const className = ok ? 'text-green-600' : 'text-red-600';
        return <span key={idx} className={className}>{ch}</span>;
      })}
    </>
  );
}

// Compute how many characters changed vs unchanged
function computeChangedUnchanged(inputText: string, outputText: string, transform: string) {
  // For Gematria: we sum up changes = count of Hebrew letters, unchanged=0
  if (transform === 'Gematria') {
    let changed = 0;
    for (const c of inputText) {
      if (isHebrewLetter(c)) {
        changed++;
      }
    }
    return { changed, unchanged: 0 };
  }

  // For Remove Niqqud: changed = number of diacritics removed
  if (transform === 'Remove Niqqud') {
    const diac = (inputText.match(/[\u0591-\u05C7]/g) || []).length;
    const changed = diac;
    const inNoSpaces = inputText.replace(/ /g, '');
    const unchanged = Math.max(0, inNoSpaces.length - diac);
    return { changed, unchanged };
  }

  // Otherwise, compare input vs output ignoring spaces
  const inNoSpaces = inputText.replace(/ /g, '');
  const outNoSpaces = outputText.replace(/ /g, '');

  let i = 0;
  let j = 0;
  let changed = 0;
  let unchanged = 0;

  while (i < inNoSpaces.length && j < outNoSpaces.length) {
    if (inNoSpaces[i] === outNoSpaces[j]) {
      unchanged++;
    } else {
      changed++;
    }
    i++;
    j++;
  }

  // anything left in either string is also changed
  changed += (inNoSpaces.length - i) + (outNoSpaces.length - j);

  return { changed, unchanged };
}
function highlightOutput(inputText: string, outputText: string, transform: string) {
  // For Gematria, just show plain text.
  if (transform === 'Gematria') {
    return <span className="dark:text-gray-50 text-gray-900">{outputText}</span>;
  }

  // Special handling for Remove Niqqud
  if (transform === 'Remove Niqqud') {
    const result: React.ReactNode[] = [];
    const changedClass = 'text-gray-900 dark:text-gray-50';   // changed: letter was modified (diacritics removed)
    const unchangedClass = 'text-gray-400 dark:text-gray-400';  // unchanged: letter had no niqqud

    // Iterate through inputText while grouping a Hebrew letter with its following diacritics.
    let i = 0;
    let key = 0;
    while (i < inputText.length) {
      const ch = inputText[i];

      // If the character isn't a Hebrew letter or is a space/punctuation, just output it.
      if (!isHebrewLetter(ch)) {
        result.push(
          <span key={key++} className={ch === ' ' ? 'text-gray-400 dark:text-gray-400' : unchangedClass}>
            {ch}
          </span>
        );
        i++;
        continue;
      }

      // It’s a Hebrew letter. Check if any following diacritics exist.
      let group = ch;
      let hadNiqqud = false;
      let j = i + 1;
      while (j < inputText.length && /[\u0591-\u05C7]/.test(inputText[j])) {
        group += inputText[j];
        hadNiqqud = true;
        j++;
      }
      // The output, after removing niqqud, is just the base letter (ch).
      result.push(
        <span key={key++} className={hadNiqqud ? changedClass : unchangedClass}>
          {ch}
        </span>
      );
      i = j;
    }
    return <>{result}</>;
  }

  // For other transforms, use the original per-character comparison approach.
  // Remove spaces from persistent comparison.
  const inNoSpaces = inputText.replace(/\s+/g, '');
  const outNoSpaces = outputText.replace(/\s+/g, '');

  const result: React.ReactNode[] = [];
  const outArr = outputText.split('');

  let currentText = '';
  let currentClass = '';
  let j = 0, i = 0;

  for (let k = 0; k < outArr.length; k++) {
    const c = outArr[k];
    const isSpace = c === ' ';
    const className = isSpace 
      ? 'text-gray-400 dark:text-gray-400'
      : (i < inNoSpaces.length && j < outNoSpaces.length && outNoSpaces[j] === inNoSpaces[i])
        ? 'text-gray-400 dark:text-gray-400'
        : 'text-gray-900 dark:text-gray-50';

    if (!isSpace && i < inNoSpaces.length && j < outNoSpaces.length) {
      i++;
      j++;
    }

    if (currentClass === '') {
      currentClass = className;
    }

    if (className !== currentClass) {
      result.push(
        <span key={result.length} className={currentClass}>{currentText}</span>
      );
      currentText = c;
      currentClass = className;
    } else {
      currentText += c;
    }
  }

  // Push the last span if there's remaining text
  if (currentText) {
    result.push(
      <span key={result.length} className={currentClass}>{currentText}</span>
    );
  }

  return <>{result}</>;
}


//////////////////////////////////////////
// LinedBox component with line numbers
//////////////////////////////////////////
interface LinedBoxProps {
  label: string;
  value: string;
  onChange?: (v: string) => void;
  readOnly?: boolean;
  showDropdown?: boolean;
  dropdownValue?: string;
  onDropdownChange?: (val: string) => void;
  onCopy?: () => void;
  changedCount?: number;
  unchangedCount?: number;
  highlight?: React.ReactNode; // highlight rendering
}

function LinedBox(props: LinedBoxProps) {
  const [isOpen, setIsOpen] = useState(false)
  const {
    label,
    value,
    onChange,
    readOnly,
    showDropdown,
    dropdownValue,
    onDropdownChange,
    onCopy,
    changedCount,
    unchangedCount,
    highlight,
  } = props;

  // We'll split the text into lines for numbering
  const lines = value.split('\n');
  const lineCount = Math.max(lines.length, 1);

  return (
    <div className="relative flex flex-col h-full">



      {/* Header row with label, optional dropdown, changed/unchanged, copy button */}
<div className="flex items-center justify-between px-2 border-b border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 h-10">        {showDropdown ? (
          <div className="relative">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="inline-flex w-full items-center justify-between gap-x-1.5  px-3 py-2 text-sm text-gray-700  hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-100 dark:ring-gray-700 dark:hover:bg-gray-700"
            >
              {dropdownValue}
              <ChevronDown className="h-4 w-4" />
            </button>

            {isOpen && (
  <div 
    className="absolute left-0 z-10 mt-2 w-56 origin-top-left rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none dark:bg-gray-800 dark:ring-gray-700 max-h-56 overflow-y-auto md:max-h-96"
  >
    {transformations.map((item) => (
      <button
        key={item.title}
        onClick={() => {
          onDropdownChange?.(item.title)
          setIsOpen(false)
        }}
        className="block w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700"
      >
        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
          {item.title}
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400">
          {item.subtitle}
        </div>
      </button>
    ))}
  </div>
)}

          </div>
        ) : (
          // Else just show the label
          <div className="font-semibold text-sm text-gray-700 dark:text-gray-100">{label}</div>
        )}
        {/* Show changed/unchanged if provided */}
        {(changedCount !== undefined && unchangedCount !== undefined) && (
          <span className="mr-2 text-sm">
            <span className="text-gray-800">Changed: {changedCount}</span>
            {' '}·{' '}
            <span className="text-gray-500">{unchangedCount}</span>
          </span>
        )}
        {/* Copy button if onCopy is provided */}
        {onCopy && (
          <button
            onClick={onCopy}
            className="fixed bottom-4 right-4 md:static flex  bg-white  items-center gap-1 px-3 py-1 text-sm border border-gray-300 dark:border-gray-500 rounded-md text-gray-600 dark:text-gray-200 hover:text-black"
          >
            <Copy size={14} /> Copy
          </button>
        )}
      </div>

      {/* Main content area with line numbers & text area or read-only highlight*/}
      <div className="flex-1 flex overflow-hidden">
        {/* Line numbers */}
        <div className="bg-gray-100 dark:bg-gray-700 border-r border-gray-300 dark:border-gray-700 poverflow-auto text-right p-2" style={{ minWidth: '2rem' }}>
          {Array.from({ length: lineCount }).map((_, i) => (
            <div key={i} className="text-xs  leading-5 text-gray-600 dark:text-gray-300">
              {i + 1}
            </div>
          ))}
        </div>

        {/* Content (textarea or read-only highlight) */}
        {readOnly ? (
          // If readOnly => show highlight or plain value
          <div className="flex-1 p-2 text-sm leading-5 whitespace-pre-wrap break-words overflow-auto dark:bg-gray-800 dark:text-gray-100">
            {highlight || value}
          </div>
        ) : (
          // Else => editable textarea
          <textarea
            className="flex-1 p-2 text-sm leading-5 whitespace-pre-wrap break-words overflow-auto focus:outline-none resize-none dark:bg-gray-800 dark:text-gray-100"
            value={value}
            onChange={(e) => onChange?.(e.target.value)}
          />
        )}
      </div>
      
    </div>
  );
}

//////////////////////////////////////////
// Main Aleph component
//////////////////////////////////////////
export default function AlephV3_1_2() {
  const [inputText, setInputText] = useState('');
  const [outputText, setOutputText] = useState('');
  const [transform, setTransform] = useState(transformations[0].title);
  const [toast, setToast] = useState('');

  useEffect(() => {
    let result = '';
    switch (transform) {
      case 'English Keyboard': {
        // Hebrew -> English via QWERTY
        result = toLatinQwerty(inputText);
        break;
      }
      case 'Paleo-Hebrew': {
        result = toPaleoHebrew(inputText);
        break;
      }
      case 'Hebrew Keyboard': {
        // English -> Hebrew mapping
        result = toKratonHebrew(inputText);
        break;
      }
      case 'Remove Niqqud': {
        result = removeNiqqud(inputText);
        break;
      }
      case 'Gematria': {
        result = toGematria(inputText);
        break;
      }
      case 'Square Hebrew': {
        result = paleoToSquare(inputText);
        break;
      }
      default:
        result = inputText;
    }
    setOutputText(result);
  }, [inputText, transform]);

  // highlight input & output, and compute changed/unchanged
  const inputHighlighted = highlightInput(inputText, transform);
  const outputHighlighted = highlightOutput(inputText, outputText, transform);
  const { changed, unchanged } = computeChangedUnchanged(inputText, outputText, transform);

  // Copy button
  function handleCopy() {
    navigator.clipboard.writeText(outputText);
    setToast('Copied!');
  }

  // Clear toast after 2s
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(''), 2000);
    return () => clearTimeout(t);
  }, [toast]);

  return (
    <div
      className="w-full h-screen flex flex-col bg-gray-200 dark:bg-gray-900 dark:text-gray-100"
      style={{ fontFamily: systemFontStack }}
    >
      {/* Header */}
      <header className="h-12 flex items-center justify-between border-b border-gray-300 dark:border-gray-700 px-4">
        <h1 className="text-xl">Aleph Tools<span className='text-sm'> V3.1.3 <span className="text-gray-500"> Beta</span></span></h1>
        <a
          href="https://d7mtg.com"
          target="_blank"
          className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
        >
          Project by D7mtg
        </a>
      </header>

      {/* Main area: responsive, stacks on mobile, side-by-side on md+ */}
      <main className="flex-1 flex flex-col md:flex-row gap-2 p-2 overflow-hidden">
        {/* Left: Input */}
        <div className="flex-1 border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-md shadow-md overflow-hidden">
          <LinedBox
            label="Input"
            value={inputText}
            onChange={(val) => setInputText(val)}
            highlight={inputHighlighted}
            unchangedCount={unchanged}
          />
        </div>

        {/* Right: Output with drop-down */}
        <div className="flex-1 border border-gray-300 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 rounded-md shadow-md overflow-hidden">
          <LinedBox
            label="Output"
            value={outputText}
            readOnly
            showDropdown
            dropdownValue={transform}
            onDropdownChange={setTransform}
            onCopy={handleCopy}
            highlight={outputHighlighted}
            changedCount={changed}
            unchangedCount={unchanged}
          />
        </div>
      </main>

      {/* Toast for copy notification */}
      {toast && (
        <div className="absolute bottom-2 right-2 bg-black text-white px-3 py-2 rounded shadow-md">
          {toast}
        </div>
      )}
      <Analytics />
    </div>
  );
}

//////////////////////////////////////////
// TEST CASES
//////////////////////////////////////////

// 1) Remove Niqqud
export function testRemoveNiqqud() {
  const input = "בּראשׁית"; // includes niqqud
  const expected = "בראשית"; // removed niqqud
  const actual = removeNiqqud(input);
  if (actual !== expected) {
    throw new Error(`RemoveNiqqud test failed. Expected '${expected}', got '${actual}'`);
  }
}

// 2) Gematria
export function testGematria() {
  const input = "אדם"; // (1 + 4 + 40) = 45
  const expected = "45";
  const actual = toGematria(input);
  if (actual !== expected) {
    throw new Error(`Gematria test failed. Expected '${expected}', got '${actual}'`);
  }
}

// 3) Paleo transform
export function testPaleoHebrew() {
  const input = "דג"; // dalet + gimel => 𐤃𐤂
  const expected = "𐤃𐤂";
  const actual = toPaleoHebrew(input);
  if (actual !== expected) {
    throw new Error(`PaleoHebrew test failed. Expected '${expected}', got '${actual}'`);
  }
}

// 4) Latin QWERTY transform
export function testLatinQwerty() {
  const input = "קרא"; // ignoring diacritics => we map each letter via hebrewToEnglishDict
  // ק => e, ר => r, א => t
  // Expected => "ert"
  const expected = "ert";
  const actual = toLatinQwerty(input);
  if (actual !== expected) {
    throw new Error(`LatinQwerty test failed. Expected '${expected}', got '${actual}'`);
  }
}
